/**
 * Compare — side-by-side competitor comparison page.
 *
 * - Up to 4 slots, each holds one URL from the user's audit history.
 * - For each slot we show the 6 category donuts (SEO, GEO, AEO, Perf,
 *   Security, Accessibility) plus the Overall score.
 * - Per-category, the highest score gets a "WINNER" badge.
 * - localStorage persists the selected URLs so you can deep-link in
 *   from the audit detail page's "Add to comparison" button.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowSquareOut, Plus, Trash, Trophy, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { shortUrl } from "@/lib/score-utils";

const STORAGE_KEY = "auditor_compare_slots";

const CATEGORIES = [
  { key: "overall",       label: "Overall",       color: "#0A0A0A" },
  { key: "seo",           label: "SEO",           color: "#1D4ED8" },
  { key: "geo",           label: "GEO",           color: "#0891B2" },
  { key: "aeo",           label: "AEO",           color: "#9333EA" },
  { key: "performance",   label: "Performance",   color: "#0F766E" },
  { key: "security",      label: "Security",      color: "#B91C1C" },
  { key: "accessibility", label: "Accessibility", color: "#6D28D9" },
];

const MAX_SLOTS = 4;

export default function ComparePage() {
  const [audits, setAudits] = useState([]);
  const [slots, setSlots] = useState([]);   // array of audit IDs
  const [picker, setPicker] = useState(null); // slot-index currently picking
  const [params] = useSearchParams();

  // Boot — fetch user audits + restore slot state. Honour ?add=<id> so
  // deep-links from the audit-detail page can pre-fill a slot.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/audits");
        const completed = (data.audits || []).filter((a) => a.scores?.overall != null);
        setAudits(completed);
        const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
        const addId = params.get("add");
        const next = stored.filter((id) => completed.some((a) => a.id === id));
        if (addId && completed.some((a) => a.id === addId) && !next.includes(addId)) {
          if (next.length < MAX_SLOTS) next.push(addId);
        }
        setSlots(next);
      } catch (err) {
        toast.error("Failed to load audits");
      }
    })();
  }, [params]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(slots));
  }, [slots]);

  const slotAudits = useMemo(
    () => slots.map((id) => audits.find((a) => a.id === id)).filter(Boolean),
    [slots, audits]
  );

  // Pre-compute the winner audit-id per category so we can highlight
  // it inside each slot card without nested loops.
  const winners = useMemo(() => {
    const out = {};
    CATEGORIES.forEach(({ key }) => {
      let best = -1;
      let bestId = null;
      slotAudits.forEach((a) => {
        const v = a.scores?.[key];
        if (v != null && v > best) { best = v; bestId = a.id; }
      });
      // Only crown a winner if at least 2 slots are filled (so single
      // audit doesn't always show a winner badge)
      if (slotAudits.length >= 2) out[key] = bestId;
    });
    return out;
  }, [slotAudits]);

  // Find one audit per *URL* (latest), so the picker doesn't show
  // five entries for the same site.
  const uniqueByUrl = useMemo(() => {
    const map = new Map();
    audits.forEach((a) => {
      if (!map.has(a.url)) map.set(a.url, a);
    });
    return Array.from(map.values());
  }, [audits]);

  const addSlot = (auditId) => {
    if (slots.includes(auditId)) { toast.info("Already in comparison"); setPicker(null); return; }
    if (slots.length >= MAX_SLOTS) { toast.error(`Max ${MAX_SLOTS} URLs at a time`); return; }
    if (picker != null && picker < slots.length) {
      // Replace the existing slot
      const next = [...slots];
      next[picker] = auditId;
      setSlots(next);
    } else {
      setSlots([...slots, auditId]);
    }
    setPicker(null);
  };

  const removeSlot = (i) => setSlots(slots.filter((_, idx) => idx !== i));
  const clearAll = () => setSlots([]);

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-2 gap-6 flex-wrap">
        <div>
          <div className="label-overline mb-2">Compare</div>
          <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter leading-none">
            Side-by-side
          </h1>
        </div>
        {slots.length > 0 && (
          <button onClick={clearAll} data-testid="compare-clear"
            className="text-xs text-ink-muted hover:text-ink inline-flex items-center gap-1.5 self-center">
            <Trash size={12} /> Clear all
          </button>
        )}
      </div>
      <p className="text-sm text-ink-muted mb-10 max-w-2xl">
        Stack up to {MAX_SLOTS} URLs across all six categories. Per category, the
        highest score earns a <Trophy size={12} weight="fill" className="inline text-warning -mt-0.5" /> WINNER badge.
        Pull audits from your own history — they must already be completed.
      </p>

      <div
        className={`grid gap-3 mb-6 ${
          slots.length <= 1 ? "lg:grid-cols-2" :
          slots.length === 2 ? "lg:grid-cols-2" :
          slots.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-4"
        }`}
        data-testid="compare-slots"
      >
        {[...Array(Math.min(MAX_SLOTS, Math.max(2, slots.length + 1)))].map((_, i) => {
          const a = slotAudits[i];
          if (a) return (
            <SlotCard key={a.id} audit={a} index={i} winners={winners}
              onRemove={() => removeSlot(i)}
              onReplace={() => setPicker(i)} />
          );
          return (
            <EmptySlot key={`empty-${i}`} index={i} onPick={() => setPicker(i)} />
          );
        })}
      </div>

      {picker !== null && (
        <PickerModal
          audits={uniqueByUrl}
          selectedIds={slots}
          onPick={addSlot}
          onClose={() => setPicker(null)}
        />
      )}

      {audits.length === 0 && (
        <div className="surface-card p-12 text-center" data-testid="compare-empty">
          <div className="font-display text-xl font-bold mb-2">No completed audits yet</div>
          <p className="text-sm text-ink-muted mb-6">
            Run a few audits first, then come back to stack them up.
          </p>
          <Link to="/app/new"
            className="inline-flex items-center gap-2 bg-ink text-white px-4 py-2.5 rounded-sm font-semibold hover:bg-brand transition-colors">
            Run an audit <ArrowSquareOut size={14} />
          </Link>
        </div>
      )}
    </AppShell>
  );
}

/* ----------------------- Slot card ------------------------------- */
function Donut({ value, color, size = 56, stroke = 5 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (Math.max(0, Math.min(100, value || 0)) / 100) * c;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={`${dash} ${c - dash}`} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center num-display text-sm font-black" style={{ color }}>
        {value ?? "—"}
      </div>
    </div>
  );
}

function SlotCard({ audit, index, winners, onRemove, onReplace }) {
  return (
    <div className="surface-card overflow-hidden" data-testid={`slot-${index}`}>
      <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="mono text-[10px] tracking-wider text-ink-muted mb-1">SLOT {index + 1}</div>
          <div className="font-semibold text-sm truncate" title={audit.url}>{shortUrl(audit.url)}</div>
          <Link to={`/app/audit/${audit.id}`} className="text-[11px] text-brand hover:underline mono">
            view full audit →
          </Link>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onReplace} className="text-ink-muted hover:text-ink p-1.5 rounded-sm" title="Replace">
            <Plus size={14} />
          </button>
          <button onClick={onRemove} className="text-critical hover:bg-critical/10 p-1.5 rounded-sm"
            data-testid={`slot-${index}-remove`}>
            <X size={14} weight="bold" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-px bg-border">
        {CATEGORIES.map(({ key, label, color }) => {
          const v = audit.scores?.[key];
          const isWinner = winners[key] === audit.id;
          return (
            <div key={key} className="bg-white px-4 py-3 flex items-center justify-between relative">
              <div>
                <div className="text-[10px] mono tracking-wider text-ink-muted">{label.toUpperCase()}</div>
                {isWinner && (
                  <div className="mono text-[9px] tracking-wider font-bold text-warning inline-flex items-center gap-1 mt-0.5">
                    <Trophy size={10} weight="fill" /> WINNER
                  </div>
                )}
              </div>
              <Donut value={v} color={color} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EmptySlot({ index, onPick }) {
  return (
    <button onClick={onPick} data-testid={`slot-empty-${index}`}
      className="surface-card border-dashed border-2 hover:border-ink transition-colors p-10 flex flex-col items-center justify-center gap-3 min-h-[360px]">
      <div className="w-12 h-12 rounded-sm border-2 border-ink-muted flex items-center justify-center">
        <Plus size={20} className="text-ink-muted" />
      </div>
      <div className="font-display font-bold">Add a URL</div>
      <div className="text-xs text-ink-muted">Pick from your audit history</div>
    </button>
  );
}

/* ----------------------- Picker modal ---------------------------- */
function PickerModal({ audits, selectedIds, onPick, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="picker-modal">
      <div className="surface-card max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div>
            <div className="label-overline mb-1">Pick an audit</div>
            <div className="text-sm text-ink-muted">{audits.length} available</div>
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink">
            <X size={18} />
          </button>
        </div>
        <div className="overflow-y-auto">
          {audits.length === 0 ? (
            <div className="p-10 text-center text-sm text-ink-muted">
              No completed audits yet.
            </div>
          ) : (
            audits.map((a) => {
              const taken = selectedIds.includes(a.id);
              return (
                <button key={a.id} onClick={() => onPick(a.id)} disabled={taken}
                  data-testid={`picker-row-${a.id}`}
                  className="w-full px-6 py-3 text-left border-b border-border last:border-0 hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">{shortUrl(a.url)}</div>
                    <div className="text-[11px] mono text-ink-muted">{new Date(a.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="num-display text-2xl font-black tabular-nums">{a.scores?.overall ?? "—"}</div>
                  {taken && <span className="mono text-[10px] tracking-wider text-ink-muted">IN USE</span>}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
